import { Users, DollarSign, Calendar, BookOpen, Check, ChevronDown, ChevronLeft, ChevronRight, Church, Search, UserRound } from "lucide-react";
import { Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ComposedChart, Cell, LabelList } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { sortGeneralFirst, sortGeneralFirstByName } from "@/lib/utils";
import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  isSameDay,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  parseISO,
} from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

/* ── Daily verse ── */
const VERSE_LIST = [
  "John 3:16", "Philippians 4:13", "Jeremiah 29:11", "Psalm 23:1", "Romans 8:28",
  "Proverbs 3:5-6", "Isaiah 41:10", "Matthew 6:33", "Psalm 46:1", "2 Timothy 1:7",
  "Romans 12:2", "Ephesians 2:8-9", "Psalm 119:105", "1 Corinthians 13:4-7",
  "Galatians 5:22-23", "Hebrews 11:1", "Joshua 1:9", "Psalm 37:4", "Matthew 11:28",
  "Isaiah 40:31", "Psalm 27:1", "Romans 5:8", "Philippians 4:6-7", "Psalm 139:14",
  "Colossians 3:23", "Micah 6:8", "Lamentations 3:22-23", "Psalm 100:4-5",
  "2 Corinthians 5:17", "1 Peter 5:7",
];
function getDailyVerseRef(): string {
  const today = new Date();
  const dayOfYear = Math.floor(
    (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000
  );
  return VERSE_LIST[dayOfYear % VERSE_LIST.length];
}

/* ── Ministry extraction from finance notes ── */
const MINISTRY_NOTE_PREFIX = "Ministry: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
function getMinistryFromNotes(notes: string | null): string | null {
  if (!notes) return null;
  const firstLine = notes.split("\n")[0];
  if (firstLine.startsWith(MINISTRY_NOTE_PREFIX))
    return firstLine.replace(MINISTRY_NOTE_PREFIX, "").trim();
  if (firstLine.startsWith(LEGACY_ORG_NOTE_PREFIX))
    return firstLine.replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  return null;
}

function getOrganizationIdsFromNotes(notes: string | null): string[] {
  if (!notes) return [];

  const orgIdsLine = notes
    .split("\n")
    .find((line) => line.startsWith("Organization IDs: "));

  if (!orgIdsLine) return [];

  return orgIdsLine
    .replace("Organization IDs: ", "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function formatEventTime(startTime: string | null): string {
  if (!startTime) return "";
  const [hRaw, mRaw] = startTime.split(":");
  const hour = Number.parseInt(hRaw, 10);
  const minute = Number.parseInt(mRaw ?? "0", 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return startTime;

  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
}

const DASHBOARD_STALE_MS = 1000 * 60 * 2;
const DASHBOARD_INCOME_COLOR = "#1e3a8a";
const DASHBOARD_EXPENSE_COLOR = "#f59e0b";

const Dashboard = () => {
  const { profile, loading: churchLoading } = useChurch();
  const { user, role } = useAuth();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess, permissionsLoading } = usePermissions();
  const churchId = profile?.id;
  const churchTitle = churchLoading ? "" : (profile?.church_name || "Dashboard");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const now = new Date();
  const isLikelyAdmin = useMemo(() => {
    const normalizedRole = (role || "").trim().toLowerCase().replace(/\s+/g, "_");
    return normalizedRole === "admin" || normalizedRole === "church_admin";
  }, [role]);

  const [calendarWeekOffset, setCalendarWeekOffset] = useState(0);
  const [selectedMinistries, setSelectedMinistries] = useState<Set<string>>(new Set());
  const [ministryFilterOpen, setMinistryFilterOpen] = useState(false);
  const [ministrySearch, setMinistrySearch] = useState("");
  const [chartPeriod, setChartPeriod] = useState<"weekly" | "monthly" | "yearly">("monthly");
  const [chartMetric, setChartMetric] = useState<"income" | "expense" | "both">("both");
  const [hoveredBar, setHoveredBar] = useState<{ key: "incomeTotal" | "expenseTotal" | "total"; index: number } | null>(null);
  const calendarDragStartX = useRef<number | null>(null);
  const [calendarDragOffset, setCalendarDragOffset] = useState(0);
  const [isCalendarDragging, setIsCalendarDragging] = useState(false);

  const resetCalendarDrag = () => {
    calendarDragStartX.current = null;
    setCalendarDragOffset(0);
    setIsCalendarDragging(false);
  };

  const handleCalendarPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button")) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    calendarDragStartX.current = e.clientX;
    setIsCalendarDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleCalendarPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (calendarDragStartX.current === null) return;
    setCalendarDragOffset(e.clientX - calendarDragStartX.current);
  };

  const handleCalendarPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (calendarDragStartX.current === null) return;
    const deltaX = e.clientX - calendarDragStartX.current;

    if (Math.abs(deltaX) >= 40) {
      setCalendarWeekOffset((p) => p + (deltaX < 0 ? 1 : -1));
    }

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    resetCalendarDrag();
  };

  /* ── User profile (display name + avatar) ── */
  const { data: userProfile } = useQuery({
    queryKey: ["user_profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("display_name, avatar_url")
        .eq("user_id", user!.id)
        .maybeSingle() as any;
      if (error) throw error;
      return data as { display_name: string | null; avatar_url: string | null } | null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  const displayName =
    userProfile?.display_name ||
    (user as any)?.user_metadata?.display_name ||
    user?.email?.split("@")[0] ||
    "User";
  const avatarUrl = userProfile?.avatar_url || null;
  const initials = displayName
    .split(" ")
    .map((n: string) => n[0]?.toUpperCase())
    .join("")
    .slice(0, 2) || "U";
  const roleName = role ? role.charAt(0).toUpperCase() + role.slice(1) : "Member";

  /* ── Daily Bible Verse ── */
  const verseRef = getDailyVerseRef();
  const { data: verse, isLoading: verseLoading } = useQuery({
    queryKey: ["daily_bible_verse", verseRef],
    queryFn: async () => {
      try {
        const res = await fetch(
          `https://bible-api.com/${encodeURIComponent(verseRef)}?translation=kjv`
        );
        if (!res.ok) throw new Error("Fetch failed");
        const json = await res.json();
        const baseText = (json.text as string)?.trim();
        const reference = (json.reference as string) || verseRef;

        if (!baseText) throw new Error("Missing verse text");

        return { text: baseText, reference };
      } catch {
        return {
          text: "For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life.",
          reference: "John 3:16",
        };
      }
    },
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });

  /* ── Members (count only — separate key from CommunityInsights which fetches rows) ── */
  const { data: memberCount = 0, isLoading: isLoadingMembers } = useQuery({
    queryKey: ["dashboard_member_count", churchId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("members")
        .select("id", { count: "exact", head: true })
        .eq("church_id", churchId!);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!churchId,
    staleTime: DASHBOARD_STALE_MS,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  /* ── Finance records (past 6 months + yearly window support) ── */
  const sixMonthsAgo = format(startOfMonth(subMonths(now, 5)), "yyyy-MM-dd");
  const yearStart = format(new Date(now.getFullYear(), 0, 1), "yyyy-MM-dd");
  const fiveYearsAgoStart = format(new Date(now.getFullYear() - 4, 0, 1), "yyyy-MM-dd");
  const queryStart = [sixMonthsAgo, yearStart, fiveYearsAgoStart].sort()[0];

  const { data: financeRecords = [], isLoading: isLoadingFinance } = useQuery({
    queryKey: ["dashboard_finance", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("id, member_name, amount, category, date, notes, record_type, created_at")
        .eq("church_id", churchId!)
        .gte("date", queryStart)
        .order("date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
    staleTime: DASHBOARD_STALE_MS,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  /* ── Organizations (for ministry filter dropdown) ── */
  const { data: organizations = [] } = useQuery({
    queryKey: ["dashboard_organizations", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("church_id", churchId!);
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as { id: string; name: string }[]);
    },
    enabled: !!churchId,
    staleTime: DASHBOARD_STALE_MS,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  const scopedFinanceOrgIds = getFinanceOrganizationIds();
  const hasScopedFinanceAccess = hasFinanceScopedAccess();
  const allowedFinanceOrgIdSet = useMemo(() => new Set(scopedFinanceOrgIds), [scopedFinanceOrgIds]);

  const visibleOrganizations = useMemo(() => {
    if (permissionsLoading && !isLikelyAdmin) return [] as { id: string; name: string }[];
    if (!hasScopedFinanceAccess) return organizations;
    return organizations.filter((org) => allowedFinanceOrgIdSet.has(org.id));
  }, [organizations, hasScopedFinanceAccess, allowedFinanceOrgIdSet, permissionsLoading, isLikelyAdmin]);

  const allowedFinanceMinistryNameSet = useMemo(
    () => new Set(visibleOrganizations.map((org) => org.name)),
    [visibleOrganizations]
  );

  const scopedFinanceRecords = useMemo(() => {
    if (permissionsLoading && !isLikelyAdmin) return [] as typeof financeRecords;
    if (!hasScopedFinanceAccess) return financeRecords;

    return financeRecords.filter((record) => {
      const ministryIds = getOrganizationIdsFromNotes((record as any).notes);
      if (ministryIds.some((id) => allowedFinanceOrgIdSet.has(id))) return true;

      const ministryName = getMinistryFromNotes((record as any).notes);
      return !!ministryName && allowedFinanceMinistryNameSet.has(ministryName);
    });
  }, [financeRecords, hasScopedFinanceAccess, allowedFinanceOrgIdSet, allowedFinanceMinistryNameSet, permissionsLoading, isLikelyAdmin]);

  const financeUiLoading = isLoadingFinance || (permissionsLoading && !isLikelyAdmin);

  /* ── Ministry names from organizations only ── */
  const ministryNames = useMemo(() => {
    return sortGeneralFirst(visibleOrganizations.map((o) => o.name));
  }, [visibleOrganizations]);

  useEffect(() => {
    setSelectedMinistries((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(Array.from(prev).filter((name) => ministryNames.includes(name)));
      return next.size === prev.size ? prev : next;
    });
  }, [ministryNames]);

  const ministryFilterLabel = useMemo(() => {
    if (selectedMinistries.size === 0) return "All Ministries";
    if (selectedMinistries.size === 1) return Array.from(selectedMinistries)[0] || "All Ministries";
    return `${selectedMinistries.size} Ministries`;
  }, [selectedMinistries]);

  const filteredMinistryNames = useMemo(() => {
    const query = ministrySearch.trim().toLowerCase();
    if (!query) return ministryNames;
    return ministryNames.filter((name) => name.toLowerCase().includes(query));
  }, [ministryNames, ministrySearch]);

  /* ── Filtered finance ── */
  const filteredFinance = useMemo(() => {
    if (selectedMinistries.size === 0) return scopedFinanceRecords;
    return scopedFinanceRecords.filter((r) => {
      const m = getMinistryFromNotes((r as any).notes);
      return !!m && selectedMinistries.has(m);
    });
  }, [scopedFinanceRecords, selectedMinistries]);

  /* ── Expense this week ── */
  const weekStart = format(startOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd");
  const weekEnd = format(endOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd");
  const monthStart = format(startOfMonth(now), "yyyy-MM-dd");
  const monthEnd = format(endOfMonth(now), "yyyy-MM-dd");
  const yearEnd = format(endOfYear(now), "yyyy-MM-dd");

  const expenseCardMeta = useMemo(() => {
    if (chartPeriod === "monthly") {
      return {
        title: "Expense This Month",
        start: monthStart,
        end: monthEnd,
        rangeLabel: `${format(startOfMonth(now), "MMM d")} – ${format(endOfMonth(now), "MMM d")}`,
      };
    }

    if (chartPeriod === "yearly") {
      return {
        title: "Expense This Year",
        start: yearStart,
        end: yearEnd,
        rangeLabel: `${format(startOfYear(now), "MMM d")} – ${format(endOfYear(now), "MMM d")}`,
      };
    }

    return {
      title: "Expense This Week",
      start: weekStart,
      end: weekEnd,
      rangeLabel: `${format(startOfWeek(now, { weekStartsOn: 0 }), "MMM d")} – ${format(endOfWeek(now, { weekStartsOn: 0 }), "MMM d")}`,
    };
  }, [chartPeriod, monthEnd, monthStart, now, weekEnd, weekStart, yearEnd, yearStart]);

  const totalExpenseCurrentPeriod = useMemo(
    () =>
      filteredFinance
        .filter(
          (r) =>
            r.date >= expenseCardMeta.start &&
            r.date <= expenseCardMeta.end &&
            (r as any).record_type === "expense"
        )
        .reduce((s, r) => s + Number(r.amount), 0),
    [filteredFinance, expenseCardMeta]
  );

  const incomeCardMeta = useMemo(() => {
    if (chartPeriod === "monthly") {
      return {
        title: "Income This Month",
        start: monthStart,
        end: monthEnd,
        rangeLabel: `${format(startOfMonth(now), "MMM d")} – ${format(endOfMonth(now), "MMM d")}`,
      };
    }

    if (chartPeriod === "yearly") {
      return {
        title: "Income This Year",
        start: yearStart,
        end: yearEnd,
        rangeLabel: `${format(startOfYear(now), "MMM d")} – ${format(endOfYear(now), "MMM d")}`,
      };
    }

    return {
      title: "Income This Week",
      start: weekStart,
      end: weekEnd,
      rangeLabel: `${format(startOfWeek(now, { weekStartsOn: 0 }), "MMM d")} – ${format(endOfWeek(now, { weekStartsOn: 0 }), "MMM d")}`,
    };
  }, [chartPeriod, monthEnd, monthStart, now, weekEnd, weekStart, yearEnd, yearStart]);

  const totalIncomeCurrentPeriod = useMemo(
    () =>
      filteredFinance
        .filter(
          (r) =>
            r.date >= incomeCardMeta.start &&
            r.date <= incomeCardMeta.end &&
            (r as any).record_type !== "expense"
        )
        .reduce((s, r) => s + Number(r.amount), 0),
    [filteredFinance, incomeCardMeta]
  );

  const chartData = useMemo(() => {
    const summarizeRange = (start: string, end: string) => {
      const incomeTotal = filteredFinance
        .filter((r) => r.date >= start && r.date <= end && (r as any).record_type !== "expense")
        .reduce((s, r) => s + Number(r.amount), 0);

      const expenseTotal = filteredFinance
        .filter((r) => r.date >= start && r.date <= end && (r as any).record_type === "expense")
        .reduce((s, r) => s + Number(r.amount), 0);

      const total =
        chartMetric === "income"
          ? incomeTotal
          : chartMetric === "expense"
            ? expenseTotal
            : Math.max(incomeTotal, expenseTotal);

      return { incomeTotal, expenseTotal, total };
    };

    if (chartPeriod === "weekly") {
      const weeks = [] as Array<{ label: string; start: string; end: string }>;
      for (let i = 5; i >= 0; i--) {
        const d = addWeeks(now, -i);
        const wStart = format(startOfWeek(d, { weekStartsOn: 0 }), "yyyy-MM-dd");
        const wEnd = format(endOfWeek(d, { weekStartsOn: 0 }), "yyyy-MM-dd");
        weeks.push({ label: format(parseISO(wStart), "MMM d"), start: wStart, end: wEnd });
      }

      return weeks.map((w) => ({
        label: w.label,
        ...summarizeRange(w.start, w.end),
      }));
    }

    if (chartPeriod === "yearly") {
      const years = [] as Array<{ label: string; start: string; end: string }>;
      for (let yearOffset = 4; yearOffset >= 0; yearOffset--) {
        const year = now.getFullYear() - yearOffset;
        const d = new Date(year, 0, 1);
        years.push({
          label: format(d, "yyyy"),
          start: format(startOfYear(d), "yyyy-MM-dd"),
          end: format(endOfYear(d), "yyyy-MM-dd"),
        });
      }

      return years.map((y) => ({
        label: y.label,
        ...summarizeRange(y.start, y.end),
      }));
    }

    // monthly: past 6 months
    const months = [] as Array<{ label: string; start: string; end: string }>;
    for (let i = 5; i >= 0; i--) {
      const d = subMonths(now, i);
      months.push({
        label: format(d, "MMM"),
        start: format(startOfMonth(d), "yyyy-MM-dd"),
        end: format(endOfMonth(d), "yyyy-MM-dd"),
      });
    }

    return months.map((m) => ({
      label: m.label,
      ...summarizeRange(m.start, m.end),
    }));
  }, [filteredFinance, chartMetric, chartPeriod, now]);

  const chartPeriodLabel = chartPeriod === "weekly" ? "Weekly" : chartPeriod === "yearly" ? "Yearly" : "Monthly";
  const chartMetricLabel = chartMetric === "both" ? "Income & Expense" : chartMetric === "income" ? "Income" : "Expense";

  const chartYAxisMax = useMemo(() => {
    const maxValue = chartData.reduce((max, point) => {
      return Math.max(max, Number(point.incomeTotal || 0), Number(point.expenseTotal || 0), Number(point.total || 0));
    }, 0);

    if (maxValue <= 0) return 1000;

    const roughStep = maxValue / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;
    const niceStepMultiplier = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    const niceStep = niceStepMultiplier * magnitude;
    return niceStep * 4;
  }, [chartData]);

  const chartYAxisTicks = useMemo(() => {
    const step = chartYAxisMax / 4;
    return [0, step, step * 2, step * 3, chartYAxisMax];
  }, [chartYAxisMax]);

  /* ── Upcoming Events ── */
  const { data: upcomingEvents = [] } = useQuery({
    queryKey: ["dashboard_upcoming_events", churchId],
    queryFn: async () => {
      const today = startOfDay(now).toISOString();
      const { data, error } = await supabase
        .from("events")
        .select("id, name, event_date, start_time, location, event_type, flyer_url")
        .eq("church_id", churchId!)
        .gte("event_date", today)
        .eq("is_active", true)
        .order("event_date", { ascending: true })
        .limit(6);
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
    staleTime: DASHBOARD_STALE_MS,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  /* ── Recent Activities ── */
  const recentActivities = useMemo(() => {
    return scopedFinanceRecords.slice(0, 8).map((r) => ({
      id: `fin-${r.id}`,
      text: `${r.category} — $${Number(r.amount).toLocaleString()}${r.member_name ? ` from ${r.member_name}` : ""}`,
      time: r.date,
    }));
  }, [scopedFinanceRecords]);

  /* ── Date strip calendar (today centered) ── */
  const calendarCenter = addDays(now, calendarWeekOffset * 7);
  const getCalendarDays = (centerDate: Date) =>
    Array.from({ length: 5 }, (_, i) => addDays(centerDate, i - 2));
  const calendarPanels = [
    getCalendarDays(addWeeks(calendarCenter, -1)),
    getCalendarDays(calendarCenter),
    getCalendarDays(addWeeks(calendarCenter, 1)),
  ];
  const todayDate = startOfDay(now);

  return (
    <div className="-mt-2 space-y-2">
      {/* ═══════ TOP BANNER ═══════ */}
      <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] items-end gap-1 md:gap-0">
            {/* Left: Church info */}
            <div className="p-3 sm:p-5 md:p-6 space-y-2 sm:space-y-3">
              <div className="flex items-start gap-3 sm:gap-5">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl border border-border/70 bg-card flex items-center justify-center overflow-hidden shrink-0">
                  {profile?.logo_url ? (
                    <img src={profile.logo_url} alt="Church logo" className="w-full h-full object-cover" />
                  ) : (
                    <Church className="w-7 h-7 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  {/* Keep the church name on one line on phones by reducing size only on mobile. */}
                  <h1 className="text-lg sm:text-2xl font-heading font-bold text-foreground tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">
                    {churchTitle}
                  </h1>
                  {(profile?.address || profile?.city || profile?.phone || profile?.email) && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[13px] sm:text-sm text-muted-foreground">
                      {[profile?.address, profile?.city, profile?.state].filter(Boolean).join(", ") && (
                        <span>{[profile?.address, profile?.city, profile?.state].filter(Boolean).join(", ")}</span>
                      )}
                      {profile?.phone && <span className="hidden md:inline">{profile.phone}</span>}
                      {profile?.email && <span className="hidden md:inline">{profile.email}</span>}
                    </div>
                  )}
                </div>
              </div>

              {profile?.bible_verse && (
                <div className="hidden md:block pl-[60px] sm:pl-[84px]">
                  <blockquote className="text-sm leading-relaxed text-foreground/80 italic">
                    &ldquo;{profile.bible_verse}&rdquo;
                  </blockquote>
                  {profile?.bible_reference && (
                    <p className="text-xs font-semibold text-primary mt-1">— {profile.bible_reference}</p>
                  )}
                </div>
              )}
            </div>

            {/* Right: Daily Scripture */}
            <div className="px-3 pb-3 pt-0 sm:p-5 md:p-6">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-primary" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">Daily Scripture</h3>
                </div>
              </div>
              {verseLoading ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-3 bg-muted rounded w-3/4" />
                </div>
              ) : verse ? (
                <div>
                  <blockquote className="text-sm leading-relaxed text-foreground/80 italic line-clamp-3">
                    &ldquo;{verse.text}&rdquo;
                  </blockquote>
                  <p className="text-xs font-semibold text-primary mt-2">— {verse.reference}</p>
                </div>
              ) : null}
            </div>
      </div>

      {/* ═══════ STATS ROW ═══════ */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-3">
        {/* Keep all three cards visible on mobile with a compact 3-column layout. */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {/* Total Members */}
        <Card
          className="min-w-0 cursor-pointer transition-all duration-300 bg-[#1e3a8a]/85 border-[#1e3a8a]/60 rounded-xl hover:-translate-y-0.5"
          onClick={() => navigate("/people?tab=members")}
        >
          <CardContent className="px-2 py-3 sm:px-5 sm:py-4">
            <div className="flex items-center gap-2 min-w-0">
              {/* Fixed square icon container prevents stretching on narrow screens. */}
              <div className="flex h-6 w-6 sm:h-12 sm:w-12 aspect-square shrink-0 items-center justify-center rounded-full bg-white/10">
                <Users className="h-3 w-3 sm:h-[1.125rem] sm:w-[1.125rem] shrink-0 text-white/80" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] sm:text-xs font-medium text-white/70 leading-tight whitespace-nowrap">Total Members</p>
                {isLoadingMembers ? (
                  <div className="mt-1 h-7 w-16 rounded bg-white/20 animate-pulse" />
                ) : (
                  <p className="text-base sm:text-2xl font-bold text-white break-words leading-tight">{memberCount}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Income (period aware from chart selector) */}
        <Card
          className="min-w-0 cursor-pointer transition-all duration-300 bg-[#1e3a8a]/85 border-[#1e3a8a]/60 rounded-xl hover:-translate-y-0.5"
          onClick={() => navigate("/finance")}
        >
          <CardContent className="px-2 py-3 sm:px-5 sm:py-4">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-6 w-6 sm:h-12 sm:w-12 aspect-square shrink-0 items-center justify-center rounded-full bg-white/10">
                <DollarSign className="h-3 w-3 sm:h-[1.125rem] sm:w-[1.125rem] shrink-0 text-white/80" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] sm:text-xs font-medium text-white/70 leading-tight whitespace-nowrap">{incomeCardMeta.title}</p>
                {financeUiLoading ? (
                  <div className="mt-1 h-7 w-28 rounded bg-white/20 animate-pulse" />
                ) : (
                  <p className="text-[13px] sm:text-2xl font-bold text-white break-words leading-tight">${totalIncomeCurrentPeriod.toLocaleString()}</p>
                )}
                <p className="text-[9px] sm:text-[11px] text-white/50 break-words leading-tight">{incomeCardMeta.rangeLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expense (period aware from chart selector) */}
        <Card
          className="min-w-0 cursor-pointer transition-all duration-300 bg-[#1e3a8a]/85 border-[#1e3a8a]/60 rounded-xl hover:-translate-y-0.5"
          onClick={() => navigate("/finance")}
        >
          <CardContent className="px-2 py-3 sm:px-5 sm:py-4">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-6 w-6 sm:h-12 sm:w-12 aspect-square shrink-0 items-center justify-center rounded-full bg-white/10">
                <DollarSign className="h-3 w-3 sm:h-[1.125rem] sm:w-[1.125rem] shrink-0 text-white/80" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] sm:text-xs font-medium text-white/70 leading-tight whitespace-nowrap">{expenseCardMeta.title}</p>
                {financeUiLoading ? (
                  <div className="mt-1 h-7 w-28 rounded bg-white/20 animate-pulse" />
                ) : (
                  <p className="text-[13px] sm:text-2xl font-bold text-white break-words leading-tight">${totalExpenseCurrentPeriod.toLocaleString()}</p>
                )}
                <p className="text-[9px] sm:text-[11px] text-white/50 break-words leading-tight">{expenseCardMeta.rangeLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Date Strip Calendar */}
        <div className="hidden md:block">
          <div
            className="flex items-center gap-1 px-2 py-2 sm:gap-2 sm:px-4 sm:py-3 select-none touch-pan-y"
            onPointerDown={handleCalendarPointerDown}
            onPointerMove={handleCalendarPointerMove}
            onPointerUp={handleCalendarPointerUp}
            onPointerCancel={resetCalendarDrag}
          >
            <button
              onClick={() => setCalendarWeekOffset((p) => p - 1)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors active:bg-slate-100 sm:h-9 sm:w-9 sm:hover:bg-slate-100"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4 text-slate-500" />
            </button>

            <div className="min-w-0 flex-1 overflow-x-hidden overflow-y-visible py-1">
              <div
                className={`flex w-[300%] ${isCalendarDragging ? "" : "transition-transform duration-200 ease-out"}`}
                style={{ transform: `translateX(calc(-33.333333% + ${calendarDragOffset}px))` }}
              >
                {calendarPanels.map((panelDays, panelIndex) => (
                  <div key={panelIndex} className="w-1/3 grid grid-cols-5 gap-0.5 sm:gap-1">
                    {panelDays.map((day) => {
                      const isToday = isSameDay(day, todayDate);
                      return (
                        <div
                          key={day.toISOString()}
                          className={`relative flex min-w-0 aspect-square sm:aspect-auto flex-col items-center justify-center px-0.5 py-1 sm:py-2 rounded-md sm:rounded-lg transform-gpu transition-transform duration-200 ease-out sm:hover:scale-110 sm:hover:z-10 ${
                            isToday
                              ? "bg-[#1e3a8a]/85 text-white"
                              : "text-slate-600 sm:hover:bg-slate-50"
                          }`}
                        >
                          <span className={`text-[8px] sm:text-[10px] font-medium uppercase leading-none ${isToday ? "text-white/75" : "text-slate-400"}`}>
                            {format(day, "MMM")}
                          </span>
                          <span className={`text-base sm:text-xl font-bold leading-none mt-0.5 ${isToday ? "text-white" : "text-slate-800"}`}>
                            {format(day, "d")}
                          </span>
                          <span className={`text-[8px] sm:text-[10px] font-medium uppercase leading-none mt-0.5 ${isToday ? "text-white/75" : "text-slate-400"}`}>
                            {format(day, "EEE")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => setCalendarWeekOffset((p) => p + 1)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors active:bg-slate-100 sm:h-9 sm:w-9 sm:hover:bg-slate-100"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>
      </div>

      {/* ═══════ MAIN CONTENT ROW ═══════ */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-3 items-start">
        {/* Left: Monthly Income Chart */}
        <Card className="rounded-xl border border-white/40 bg-white/55 backdrop-blur-md shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
          <CardHeader className="pb-2 pt-4 px-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <DropdownMenu
                open={ministryFilterOpen}
                onOpenChange={(open) => {
                  setMinistryFilterOpen(open);
                  if (!open) setMinistrySearch("");
                }}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="ml-3 sm:ml-6 h-7 w-[150px] px-2 text-[11px] rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors flex items-center gap-1.5"
                  >
                    <span className="truncate text-left flex-1">{ministryFilterLabel}</span>
                    <ChevronDown className="w-2.5 h-2.5 text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-[210px] rounded-lg border border-slate-200 p-1.5 shadow-lg"
                >
                  <div className="mb-1 space-y-1.5 rounded-md border border-slate-100 p-1.5">
                    <div>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Type</p>
                      <div className="flex gap-1">
                        {([
                          { key: "both", label: "All" },
                          { key: "income", label: "Income" },
                          { key: "expense", label: "Expense" },
                        ] as const).map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => setChartMetric(option.key)}
                            className={cn(
                              "rounded-md px-2 py-1 text-[10px] font-medium transition-colors",
                              chartMetric === option.key
                                ? option.key === "income"
                                  ? "bg-[#1e3a8a] text-white"
                                  : option.key === "expense"
                                    ? "bg-[#f59e0b] text-white"
                                    : "bg-slate-700 text-white"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            )}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative mb-1">
                    <Search className="pointer-events-none absolute left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={ministrySearch}
                      onChange={(e) => setMinistrySearch(e.target.value)}
                      placeholder="Search"
                      className="h-6 rounded-md border-slate-200 pl-6 text-[11px]"
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>

                  <ScrollArea className="max-h-48">
                    <div className="space-y-0">
                      {filteredMinistryNames.length === 0 ? (
                        <p className="px-1.5 py-1 text-[10px] text-muted-foreground">No ministries found.</p>
                      ) : (
                        filteredMinistryNames.map((name) => {
                          const checked = selectedMinistries.has(name);
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() => {
                                setSelectedMinistries((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(name)) next.delete(name);
                                  else next.add(name);
                                  return next;
                                });
                              }}
                              className="w-full flex items-center gap-1.5 rounded px-1.5 py-[3px] hover:bg-gray-100 transition-colors"
                            >
                              <span
                                className={cn(
                                  "inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-[2px] border",
                                  checked
                                    ? "border-[#1e3a8a] bg-[#1e3a8a]"
                                    : "border-slate-300 bg-white"
                                )}
                              >
                                {checked && <Check className="h-2 w-2 text-white" />}
                              </span>
                              <span className="text-[11px] leading-tight text-foreground">{name}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="flex items-center gap-1 ml-auto">
                {([
                  { key: "weekly", label: "Weekly" },
                  { key: "monthly", label: "Monthly" },
                  { key: "yearly", label: "Yearly" },
                ] as const).map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setChartPeriod(option.key)}
                    className={cn(
                      "rounded-md px-2 py-1 text-[10px] font-medium transition-colors",
                      chartPeriod === option.key
                        ? "bg-[#1e3a8a] text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            {financeUiLoading ? (
              <div className="h-[280px] grid grid-cols-6 items-end gap-3 px-2 animate-pulse">
                {[90, 120, 150, 110, 170, 130].map((height, idx) => (
                  <div key={idx} className="rounded-t-md bg-slate-200/80" style={{ height }} />
                ))}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 20, right: isMobile ? 16 : 8, left: isMobile ? 16 : 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: isMobile ? 12 : 14, fill: "#94a3b8" }}
                    stroke="transparent"
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    hide={isMobile}
                    tick={{ fontSize: 10, fill: "#94a3b8" }}
                    stroke="transparent"
                    tickLine={false}
                    axisLine={false}
                    domain={[0, chartYAxisMax]}
                    ticks={chartYAxisTicks}
                    tickFormatter={(v: number) =>
                      v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
                    }
                  />
                  {chartMetric === "both" ? (
                    <>
                      <Bar
                        dataKey="incomeTotal"
                        fill={DASHBOARD_INCOME_COLOR}
                        fillOpacity={1}
                        radius={[6, 6, 0, 0]}
                        barSize={isMobile ? 20 : 30}
                        isAnimationActive={false}
                        onMouseEnter={(_, index) => {
                          if (typeof index === "number") setHoveredBar({ key: "incomeTotal", index });
                        }}
                        onMouseLeave={() => setHoveredBar(null)}
                      >
                        {chartData.map((_, idx) => (
                          (() => {
                            const isHovered = hoveredBar?.key === "incomeTotal" && hoveredBar?.index === idx;
                            const hasOtherHover = !!hoveredBar && !isHovered;
                            return (
                          <Cell
                            key={`income-${idx}`}
                            fill={DASHBOARD_INCOME_COLOR}
                            style={{
                              cursor: "pointer",
                              transition: "opacity 180ms ease, filter 180ms ease",
                              opacity: hasOtherHover ? 0.7 : 1,
                              filter: isHovered ? "brightness(1.14) drop-shadow(0 2px 6px rgba(30,58,138,0.28))" : "brightness(1)",
                            }}
                          />
                            );
                          })()
                        ))}
                        <LabelList
                          dataKey="incomeTotal"
                          position="top"
                          isAnimationActive={false}
                          content={(props: any) => {
                            const index = typeof props.index === "number" ? props.index : -1;
                            const isHovered = hoveredBar?.key === "incomeTotal" && hoveredBar?.index === index;
                            const centerX = Number(props.x ?? 0) + Number(props.width ?? 0) / 2;
                            return (
                              <text
                                x={centerX}
                                y={props.y}
                                dy={-6}
                                textAnchor="middle"
                                fill={DASHBOARD_INCOME_COLOR}
                                style={{
                                  fontSize: isHovered ? 11 : 9,
                                  fontWeight: 600,
                                  pointerEvents: "none",
                                }}
                              >
                                {`$${Number(props.value || 0).toLocaleString()}`}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                      <Bar
                        dataKey="expenseTotal"
                        fill={DASHBOARD_EXPENSE_COLOR}
                        fillOpacity={1}
                        radius={[6, 6, 0, 0]}
                        barSize={isMobile ? 20 : 30}
                        isAnimationActive={false}
                        onMouseEnter={(_, index) => {
                          if (typeof index === "number") setHoveredBar({ key: "expenseTotal", index });
                        }}
                        onMouseLeave={() => setHoveredBar(null)}
                      >
                        {chartData.map((_, idx) => (
                          (() => {
                            const isHovered = hoveredBar?.key === "expenseTotal" && hoveredBar?.index === idx;
                            const hasOtherHover = !!hoveredBar && !isHovered;
                            return (
                          <Cell
                            key={`expense-${idx}`}
                            fill={DASHBOARD_EXPENSE_COLOR}
                            style={{
                              cursor: "pointer",
                              transition: "opacity 180ms ease, filter 180ms ease",
                              opacity: hasOtherHover ? 0.7 : 1,
                              filter: isHovered ? "brightness(1.12) drop-shadow(0 2px 6px rgba(245,158,11,0.28))" : "brightness(1)",
                            }}
                          />
                            );
                          })()
                        ))}
                        <LabelList
                          dataKey="expenseTotal"
                          position="top"
                          isAnimationActive={false}
                          content={(props: any) => {
                            const index = typeof props.index === "number" ? props.index : -1;
                            const isHovered = hoveredBar?.key === "expenseTotal" && hoveredBar?.index === index;
                            const centerX = Number(props.x ?? 0) + Number(props.width ?? 0) / 2;
                            return (
                              <text
                                x={centerX}
                                y={props.y}
                                dy={-6}
                                textAnchor="middle"
                                fill={DASHBOARD_EXPENSE_COLOR}
                                style={{
                                  fontSize: isHovered ? 11 : 9,
                                  fontWeight: 600,
                                  pointerEvents: "none",
                                }}
                              >
                                {`$${Number(props.value || 0).toLocaleString()}`}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    </>
                  ) : (
                    <Bar
                      dataKey="total"
                      fill={chartMetric === "income" ? DASHBOARD_INCOME_COLOR : DASHBOARD_EXPENSE_COLOR}
                      fillOpacity={1}
                      radius={[6, 6, 0, 0]}
                      barSize={isMobile ? 26 : 40}
                      isAnimationActive={false}
                      onMouseEnter={(_, index) => {
                        if (typeof index === "number") setHoveredBar({ key: "total", index });
                      }}
                      onMouseLeave={() => setHoveredBar(null)}
                    >
                      {chartData.map((_, idx) => (
                        (() => {
                          const isHovered = hoveredBar?.key === "total" && hoveredBar?.index === idx;
                          const hasOtherHover = !!hoveredBar && !isHovered;
                          return (
                        <Cell
                          key={idx}
                          fill={chartMetric === "income" ? DASHBOARD_INCOME_COLOR : DASHBOARD_EXPENSE_COLOR}
                          style={{
                            cursor: "pointer",
                            transition: "opacity 180ms ease, filter 180ms ease",
                            opacity: hasOtherHover ? 0.7 : 1,
                            filter: isHovered
                              ? chartMetric === "income"
                                ? "brightness(1.14) drop-shadow(0 2px 6px rgba(30,58,138,0.28))"
                                : "brightness(1.12) drop-shadow(0 2px 6px rgba(245,158,11,0.28))"
                              : "brightness(1)",
                          }}
                        />
                          );
                        })()
                      ))}
                      <LabelList
                        dataKey="total"
                        position="top"
                        isAnimationActive={false}
                        content={(props: any) => {
                          const index = typeof props.index === "number" ? props.index : -1;
                          const isHovered = hoveredBar?.key === "total" && hoveredBar?.index === index;
                          const textColor = chartMetric === "income" ? DASHBOARD_INCOME_COLOR : DASHBOARD_EXPENSE_COLOR;
                          const centerX = Number(props.x ?? 0) + Number(props.width ?? 0) / 2;
                          return (
                            <text
                              x={centerX}
                              y={props.y}
                              dy={-6}
                              textAnchor="middle"
                              fill={textColor}
                              style={{
                                fontSize: isHovered ? 12 : 10,
                                fontWeight: 700,
                                pointerEvents: "none",
                              }}
                            >
                              {`$${Number(props.value || 0).toLocaleString()}`}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}
            <p className="mt-2 text-center text-sm font-light text-black">
              {selectedMinistries.size === 0
                ? `${chartPeriodLabel} ${chartMetricLabel}`
                : selectedMinistries.size === 1
                  ? `${ministryFilterLabel} ${chartPeriodLabel} ${chartMetricLabel}`
                  : `${ministryFilterLabel} ${chartPeriodLabel} ${chartMetricLabel}`}
            </p>

            {/* Mobile-only calendar sits below the chart instead of the header area. */}
            <div className="mt-3 md:hidden">
              <div
                className="flex items-center gap-1 px-2 py-2 select-none touch-pan-y"
                onPointerDown={handleCalendarPointerDown}
                onPointerMove={handleCalendarPointerMove}
                onPointerUp={handleCalendarPointerUp}
                onPointerCancel={resetCalendarDrag}
              >
                <button
                  onClick={() => setCalendarWeekOffset((p) => p - 1)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors active:bg-slate-100"
                  aria-label="Previous week"
                >
                  <ChevronLeft className="h-4 w-4 text-slate-500" />
                </button>

                <div className="min-w-0 flex-1 overflow-x-hidden overflow-y-visible py-1">
                  <div
                    className={`flex w-[300%] ${isCalendarDragging ? "" : "transition-transform duration-200 ease-out"}`}
                    style={{ transform: `translateX(calc(-33.333333% + ${calendarDragOffset}px))` }}
                  >
                    {calendarPanels.map((panelDays, panelIndex) => (
                      <div key={panelIndex} className="w-1/3 grid grid-cols-5 gap-0.5">
                        {panelDays.map((day) => {
                          const isToday = isSameDay(day, todayDate);
                          return (
                            <div
                              key={day.toISOString()}
                              className={`relative flex min-w-0 aspect-square flex-col items-center justify-center px-0.5 py-1 rounded-md transform-gpu transition-transform duration-200 ease-out ${
                                isToday
                                  ? "bg-[#1e3a8a]/85 text-white"
                                  : "text-slate-600"
                              }`}
                            >
                              <span className={`text-[8px] font-medium uppercase leading-none ${isToday ? "text-white/75" : "text-slate-400"}`}>
                                {format(day, "MMM")}
                              </span>
                              <span className={`text-base font-bold leading-none mt-0.5 ${isToday ? "text-white" : "text-slate-800"}`}>
                                {format(day, "d")}
                              </span>
                              <span className={`text-[8px] font-medium uppercase leading-none mt-0.5 ${isToday ? "text-white/75" : "text-slate-400"}`}>
                                {format(day, "EEE")}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setCalendarWeekOffset((p) => p + 1)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors active:bg-slate-100"
                  aria-label="Next week"
                >
                  <ChevronRight className="h-4 w-4 text-slate-500" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right: Upcoming Events */}
        <div className="flex flex-col gap-3 h-full">
          {/* Upcoming Events */}
          <Card
            className="transition-all duration-300 bg-white/60 backdrop-blur-md border-white/40 rounded-xl flex-1"
            onClick={() => navigate("/events")}
          >
            <CardContent className="p-0 overflow-hidden">
              <div className="flex items-center gap-1.5 bg-[#1e3a8a]/85 backdrop-blur-sm px-3 py-2 rounded-t-xl border-b border-white/10 cursor-pointer">
                <div className="p-1.5 bg-white/10 rounded-lg">
                  <Calendar className="w-3.5 h-3.5 text-white" />
                </div>
                <p className="text-sm font-semibold text-white cursor-pointer">Upcoming Events</p>
              </div>
              <div className="p-3">
                {upcomingEvents.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No upcoming events</p>
                ) : (
                  <div className="space-y-0">
                    {upcomingEvents.slice(0, 5).map((e) => (
                      <div
                        key={e.id}
                        className="group flex items-center gap-3 px-2 py-2 rounded-lg odd:bg-slate-50 cursor-pointer border border-transparent hover:bg-slate-200 hover:border-slate-200 transition-colors"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          navigate(`/events?eventId=${e.id}`);
                        }}
                      >
                        {/* Flyer thumbnail */}
                        {e.flyer_url ? (
                          <img src={e.flyer_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            <Calendar className="w-4 h-4 text-slate-300" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-700 truncate cursor-pointer">
                            {e.name}
                          </p>
                          <p className="text-[11px] text-slate-400 truncate">
                            {e.event_type && <span className="capitalize">{e.event_type}</span>}
                            {e.event_type && (e.start_time || e.location) && " · "}
                            {e.start_time && <span>{formatEventTime(e.start_time)}</span>}
                            {e.start_time && e.location && " · "}
                            {e.location && <span>{e.location}</span>}
                          </p>
                        </div>
                        <p className="text-[11px] text-slate-500 ml-2 shrink-0">
                          {format(parseISO(e.event_date), "MMM d")}
                        </p>
                      </div>
                    ))}
                    {upcomingEvents.length > 5 && (
                      <p className="text-[10px] text-slate-500 pt-1 inline-flex items-center cursor-pointer transition-all duration-200 hover:text-primary hover:translate-x-0.5">
                        +{upcomingEvents.length - 5} more events
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;